import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';
import {
  FAMILY_RELATIONSHIP_TYPES,
  getCancerHistoriesArray,
  getCancerTypeName,
  PATIENT_THEME,
  ROLE_COLORS as PATIENT_ROLE_COLORS,
  ROLE_TYPES as PATIENT_ROLE_TYPES
} from '../../types/patient';
import { Nurse } from '../../types/nurse';
import { GenderAgeDisplay, NameNicknameDisplay } from '../common';
import { formatDateWithTime, getHumanReadableTimeAgo } from '../../utils/dateUtils';

interface BlockedPatientsSectionProps {
  nurse: Nurse;
}

const BlockedPatientsSection: React.FC<BlockedPatientsSectionProps> = ({ nurse }) => {
  const navigate = useNavigate();
  // ブロックしている患者がいない場合は何も表示しない
  if (!nurse?.blocked_patients || nurse.blocked_patients.length === 0) {
    return null;
  }
  return (
    <Card
      sx={{
        mb: 4,
        border: `1px solid #e0e0e0`,
        boxShadow: `0 1px 4px rgba(0, 0, 0, 0.08)`,
        backgroundColor: '#fffde7' // 薄い黄色の背景
      }}>
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                対話を拒否している患者
              </Typography>
              {nurse.blocked_patients.length >= 5 && (
                <Chip
                  label={`${nurse.blocked_patients.length}人`}
                  size="small"
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    color: 'rgba(0, 0, 0, 0.87)',
                    borderWidth: 1.5
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2, backgroundColor: '#fff176' }}/>

            <Box
              sx={{
                mt: 2,
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '20px',
                  background: `linear-gradient(to top, ${PATIENT_THEME.LIST.BACKGROUND}CC, ${PATIENT_THEME.LIST.BACKGROUND}00)`,
                  pointerEvents: 'none',
                  zIndex: 1,
                  display: 'block'
                }
              }}
            >
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 1,
                  maxHeight: '500px', // 最大高さを指定
                  overflow: 'auto', // スクロール可能に
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    backgroundColor: `${PATIENT_THEME.LIST.BACKGROUND}`,
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: `${PATIENT_THEME.LIST.BORDER}`,
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: `${PATIENT_THEME.LIST.DARK}`,
                    }
                  }
                }}
              >
                <Table
                  stickyHeader
                  aria-label="対話を拒否している患者"
                  sx={{
                    "& .MuiTableHead-root": {
                      position: "sticky",
                      top: 0,
                      zIndex: 2
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      backgroundColor: `${PATIENT_THEME.LIST.BACKGROUND} !important`,
                      borderBottom: `2px solid ${PATIENT_THEME.LIST.BORDER}`
                    }
                  }}
                >
                  <TableHead sx={{
                    backgroundColor: PATIENT_THEME.LIST.BACKGROUND,
                    borderBottom: `2px solid ${PATIENT_THEME.LIST.BORDER}`
                  }}>
                    <TableRow>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ロール
                      </TableCell>
                      <TableCell width="25%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        名前 / ニックネーム
                      </TableCell>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        性別（年齢）
                      </TableCell>
                      <TableCell width="15%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        がん疾患歴
                      </TableCell>
                      <TableCell width="10%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        ブロック日時
                      </TableCell>
                      <TableCell width="30%" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                        {/*スペーサー*/}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nurse.blocked_patients
                    // ブロック日時の降順（最新のブロックが上）でソート
                    .slice()
                    .sort((a, b) => {
                      // ブロック日時がない場合は後ろに
                      if (!a.blocked_at) return 1;
                      if (!b.blocked_at) return -1;
                      // どちらもブロック日時がある場合は降順（新しい方が前）
                      return new Date(b.blocked_at).getTime() - new Date(a.blocked_at).getTime();
                    })
                    .map((patient) => (
                      <TableRow
                        key={patient.id}
                        hover
                        onClick={() => navigate(`/patients/${patient.id}`)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          },
                          ...(patient.resigned_at ? {
                            backgroundColor: '#fcf5f5',
                            opacity: 0.8,
                            '& td': { color: '#777' },
                          } : {})
                        }}
                      >
                        {/* ロール */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              color: PATIENT_ROLE_COLORS[patient.role_type as keyof typeof PATIENT_ROLE_COLORS] || '#757575',
                              fontSize: '0.75rem',
                              borderLeft: `3px solid ${PATIENT_ROLE_COLORS[patient.role_type as keyof typeof PATIENT_ROLE_COLORS] || '#757575'}`,
                              pl: 1,
                              py: 0.3,
                              opacity: 0.85,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {patient.role_type === 'FAMILY' && patient.family_relationship_type
                              ? (FAMILY_RELATIONSHIP_TYPES[patient.family_relationship_type as keyof typeof FAMILY_RELATIONSHIP_TYPES] || patient.family_relationship_type)
                              : PATIENT_ROLE_TYPES[patient.role_type as keyof typeof PATIENT_ROLE_TYPES]
                            }
                          </Typography>
                        </TableCell>

                        {/* 患者名とニックネーム */}
                        <TableCell>
                          <NameNicknameDisplay
                            name={patient.name}
                            nickname={patient.nickname}
                            nameFontWeight="normal"
                            nicknameValueColor="#000000"
                            nicknameValueFontWeight="bold"
                            isTestAccount={patient.is_test_account}
                          />
                        </TableCell>

                        {/* 性別（年齢） */}
                        <TableCell>
                          <GenderAgeDisplay
                            genderType={patient.gender_type || ''}
                            birthday={patient.birthday || ''}
                            fontSize="0.9rem"
                          />
                        </TableCell>

                        {/* がん疾患歴 */}
                        <TableCell>
                          {(() => {
                            try {
                              const histories = getCancerHistoriesArray(patient.cancer_histories);
                              if (!histories || histories.length === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                    がん疾患なし
                                  </Typography>
                                );
                              }
                              // 各がん種類をマッピングして取得
                              const cancerTypes = histories
                              .filter(h => h && typeof h === 'object')
                              .map(h => {
                                const cancerType = h.cancer_type || '';
                                let displayName = '';
                                // 'OTHER'の場合はotherフィールドを使用
                                if (cancerType === 'OTHER' && h.other) {
                                  displayName = h.other.length > 7 ?
                                    `${h.other.substring(0, 7)}...` : h.other;
                                } else {
                                  displayName = getCancerTypeName(cancerType);
                                }
                                // 診断日の処理
                                let diagnosisDate = '(不明)';
                                if (h.year || h.month) {
                                  const year = h.year || '??';
                                  const month = h.month || '??';
                                  diagnosisDate = `(${year}/${month})`;
                                }
                                return `${displayName} ${diagnosisDate}`;
                              })
                              .filter(Boolean);
                              if (cancerTypes.length > 0) {
                                return (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: '0.85rem',
                                      fontWeight: 'medium'
                                    }}
                                  >
                                    {cancerTypes.join('、')}
                                  </Typography>
                                );
                              }
                              return (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                  がん疾患なし
                                </Typography>
                              );
                            } catch (e) {
                              return (
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                  がん疾患なし
                                </Typography>
                              );
                            }
                          })()}
                        </TableCell>

                        {/* ブロック日時 */}
                        <TableCell>
                          {patient.blocked_at ? (
                            <Tooltip
                              title={`ブロック日時: ${formatDateWithTime(patient.blocked_at)}`}
                              arrow
                              placement="left"
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 'bold',
                                    ...(patient.resigned_at ? { opacity: 0.6 } : {})
                                  }}
                                >
                                  {getHumanReadableTimeAgo(patient.blocked_at)}
                                </Typography>
                                <AccessTimeIcon fontSize="small" sx={{ fontSize: '0.9rem', opacity: 0.7, ml: 0.5 }}/>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.85rem',
                                fontStyle: 'italic',
                                ...(patient.resigned_at ? { opacity: 0.6 } : {})
                              }}
                            >
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
export default BlockedPatientsSection;
